import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Camera, Trash2, CheckCircle2, X, DollarSign, PenLine, RotateCcw, Loader2, ImageIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { getDLToken, ensureDLToken } from "@/lib/dlToken";
import { fmtDateTime } from "@/lib/datetime";
import { toast } from "sonner";

// `PhotoSlot` is declared OUTSIDE the parent so it has a stable identity across
// re-renders. When it was declared inside `PODCapture`, every parent re-render
// (e.g. on every keystroke in the comments box) gave React a brand-new
// component type. React would unmount the previous DOM subtree — including the
// hidden `<input type="file">` — and the change event fired by iOS Safari (when
// the camera roll closes) would land on a detached element, silently dropping
// the photo. That's the "I have to try 2–3 times" bug.
function PhotoSlot({ label, preview, onChange, onClear, testId }) {
  const [busy, setBusy] = React.useState(false);
  // Resetting the input value on change so re-picking the same file fires
  // another change event. iOS in particular swallows the change when the
  // selected file matches the previous value.
  const handleChange = async (e) => {
    const fileSelected = !!e.target.files?.[0];
    if (fileSelected) setBusy(true);
    try {
      await Promise.resolve(onChange(e));
    } finally {
      // Give iOS a beat to finish reading the image off the camera roll so the
      // user actually SEES the spinner. Without this, the thumbnail can pop in
      // before the indicator has a chance to render and the user thinks
      // nothing happened.
      setTimeout(() => setBusy(false), 350);
      // Defer reset so React can read e.target.files[0] in the parent first.
      setTimeout(() => { if (e.target) e.target.value = ""; }, 0);
    }
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1.5 relative">
        <label className="flex items-center justify-center gap-2 h-32 rounded-xl border-2 border-dashed border-border bg-secondary/40 hover:bg-secondary cursor-pointer transition-colors" data-testid={`${testId}-label`}>
          {preview ? (
            <img src={preview} alt="POD" className="h-full w-full object-cover rounded-xl" />
          ) : (
            <div className="flex flex-col items-center text-muted-foreground">
              <Camera className="h-6 w-6" />
              <span className="text-sm mt-1">Tap to add photo</span>
            </div>
          )}
          <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={handleChange} data-testid={testId} disabled={busy} />
        </label>
        {busy && (
          <div
            className="absolute inset-0 rounded-xl bg-black/55 grid place-items-center pointer-events-none"
            data-testid={`${testId}-busy`}
          >
            <div className="flex flex-col items-center gap-1.5">
              <Loader2 className="h-5 w-5 text-white animate-spin" />
              <span className="text-[11px] uppercase tracking-wider text-white">Uploading…</span>
            </div>
          </div>
        )}
        {preview && !busy && (
          <button
            type="button"
            // stopPropagation so the surrounding <label> (which would re-open
            // the file picker on iOS) does NOT receive this tap.
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClear(); }}
            className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80"
            data-testid={`${testId}-clear`}
            aria-label="Remove photo"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function PODCapture({ order, onCompleted, onSubmitInBackground, mode = "deliver", requireSignature = true, allowStatusPicker = false }) {
  // mode:
  //  - "deliver" (default): driver / dispatcher / super_admin completing a delivery.
  //                          Both photos required, copay UI shown, POST /deliver.
  //  - "correct": super_admin overwriting POD on an already-delivered order.
  //               All fields optional, copay UI hidden, POST /pod, no delivered_at change.
  //
  // allowStatusPicker: when true (super_admin / dispatcher completing on the
  // driver's behalf from OrdersTable) a Delivered / Failed pill picker is
  // rendered at the top of the sheet so the operator can also close the stop
  // as Failed (POST /fail with a reason) without opening a separate flow.
  // The Failed path skips the photo / signature / copay requirements.
  //
  // requireSignature: when false (set by the driver page for pickup-type orders
  // where the customer isn't physically present to sign), the signature pad UI
  // is hidden and signature is removed from the validation checklist. Photos +
  // comment are still required just like a regular delivery.
  //
  // onSubmitInBackground (driver-side optimistic path):
  //   When provided, this component validates locally + builds the FormData
  //   then hands it to the parent via `onSubmitInBackground(orderId, fd)` and
  //   returns IMMEDIATELY. The parent is responsible for closing the sheet
  //   and POSTing to /deliver in the background. This lets the driver UI
  //   feel instant even when photos take seconds to upload.
  const isCorrect = mode === "correct";
  const sigRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [sigOpen, setSigOpen] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photo2, setPhoto2] = useState(null);
  const [photoPreview2, setPhotoPreview2] = useState(null);
  const [photo3, setPhoto3] = useState(null); // recipient ID photo — required for CONTROLLED medications
  const [photoPreview3, setPhotoPreview3] = useState(null);

  // Detect CONTROLLED medications from the order's items so we can require a 3rd photo
  // (recipient ID) at delivery time.
  const itemsText = ((order?.items) || []).map((it) => (it?.name || "").toLowerCase()).join(" | ");
  const isControlled = itemsText.includes("controlled");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill the existing comment when correcting an already-delivered order.
  useEffect(() => {
    if (mode === "correct") {
      setComment(order?.pod_comment || "");
      setNewStatus(order?.status || "delivered");
      setFailureReason(order?.failure_reason || "");
    }
  }, [mode, order?.id, order?.pod_comment, order?.status, order?.failure_reason]);

  // Super-admin status override on the Correct-POD sheet. Starts on the
  // order's current status so a save without touching the radios is a
  // no-op statuswise; flipping to the other value sends `new_status`.
  const [newStatus, setNewStatus] = useState(order?.status || "delivered");
  const [failureReason, setFailureReason] = useState("");

  // Outcome pill for the "complete on driver's behalf" sheet (super_admin /
  // dispatcher). Not used in driver mode. When set to "failed" the sheet
  // switches to a slim "reason + optional photo" form and posts to /fail
  // instead of /deliver on submit.
  const [deliverAction, setDeliverAction] = useState("delivered");
  const failMode = allowStatusPicker && !isCorrect && deliverAction === "failed";

  // Copay collection (only relevant when order has a positive copay AND we're
  // taking the original delivery — in "correct" mode copay was already settled
  // during the initial delivery).
  const copayAmount = typeof order?.copay === "number" && order.copay > 0 ? order.copay : 0;
  const requiresCopay = !isCorrect && !failMode && copayAmount > 0;
  // null = not chosen yet, true = collected (cash), false = declined
  const [copayCollected, setCopayCollected] = useState(null);

  // Each photo handler is async because we run a client-side compressor
  // (`compressImage`) before storing the File in state. The compressor
  // downsizes camera-fresh photos (often 4–8 MB) to ~1024×1024 JPEG at q=0.82
  // — typical result is 150–350 KB, with no perceptible quality loss on a
  // phone screen. The `PhotoSlot` component awaits these handlers and shows
  // its "Uploading…" spinner while compression runs (a few hundred ms).
  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const compressed = await compressImage(f);
    setPhoto(compressed);
    setPhotoPreview(URL.createObjectURL(compressed));
  };
  const onPhoto2 = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const compressed = await compressImage(f);
    setPhoto2(compressed);
    setPhotoPreview2(URL.createObjectURL(compressed));
  };
  const onPhoto3 = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const compressed = await compressImage(f);
    setPhoto3(compressed);
    setPhotoPreview3(URL.createObjectURL(compressed));
  };
  const clearPhoto = (which) => {
    if (which === 1) { setPhoto(null); setPhotoPreview(null); }
    else if (which === 2) { setPhoto2(null); setPhotoPreview2(null); }
    else { setPhoto3(null); setPhotoPreview3(null); }
  };

  // "Compare previous image" — pull Photo 1 AND Photo 2 from the most recent
  // DELIVERED order for this patient so the driver can visually confirm they
  // are at the right doorstep before capturing new photos. The modal has two
  // tabs (Photo 1 / Photo 2) — same reference delivery for both so they never
  // mix visits. See backend endpoint GET /orders/{order_id}/previous-delivery-photo.
  const [prevOpen, setPrevOpen] = useState(false);
  const [prevBusy, setPrevBusy] = useState(false);
  const [prevTab, setPrevTab] = useState(1); // 1 | 2
  // Per-slot cache so switching tabs doesn't refetch: { 1: url|null, 2: url|null }
  const [prevPhotos, setPrevPhotos] = useState({ 1: null, 2: null });
  const [prevErrors, setPrevErrors] = useState({ 1: null, 2: null }); // per-slot: "NO_PREVIOUS_DELIVERY" | "NO_PREVIOUS_PHOTO_2" | "GENERIC"
  const [prevDeliveredAt, setPrevDeliveredAt] = useState(null);

  const fetchPrevPhoto = async (slot) => {
    try {
      await ensureDLToken();
      const res = await api.get(`/orders/${order.id}/previous-delivery-photo`, {
        params: { photo: slot },
        responseType: "blob",
      });
      const objUrl = URL.createObjectURL(res.data);
      setPrevPhotos((prev) => ({ ...prev, [slot]: objUrl }));
      setPrevErrors((prev) => ({ ...prev, [slot]: null }));
      if (res.headers?.["x-delivered-at"]) setPrevDeliveredAt(res.headers["x-delivered-at"]);
      return "ok";
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      let code = "GENERIC";
      if (status === 404) code = detail || (slot === 2 ? "NO_PREVIOUS_PHOTO_2" : "NO_PREVIOUS_DELIVERY");
      setPrevErrors((prev) => ({ ...prev, [slot]: code }));
      return code;
    }
  };

  const openPreviousImage = async () => {
    if (!order?.id) return;
    setPrevOpen(true);
    setPrevBusy(true);
    setPrevTab(1);
    setPrevErrors({ 1: null, 2: null });
    setPrevPhotos({ 1: null, 2: null });
    setPrevDeliveredAt(null);
    // Fetch both in parallel — Photo 1 gates the modal (if patient has no
    // prior delivery, we display the big amber "NO PREVIOUS DELIVERY" panel
    // regardless of Photo 2). Photo 2 loads silently in the background so
    // the tab switch is instant.
    await Promise.allSettled([fetchPrevPhoto(1), fetchPrevPhoto(2)]);
    setPrevBusy(false);
  };

  // Horizontal swipe handlers on the modal image area so drivers can flip
  // between Photo 1 and Photo 2 one-handed (in addition to tapping the tabs).
  // Threshold of 40 px is small enough to feel snappy on a phone yet large
  // enough that a slight thumb wobble won't accidentally switch tabs.
  const prevSwipeRef = useRef({ x: null, y: null, active: false });
  const SWIPE_THRESHOLD = 40;
  const onPrevTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    prevSwipeRef.current = { x: t.clientX, y: t.clientY, active: true };
  };
  const onPrevTouchEnd = (e) => {
    const state = prevSwipeRef.current;
    if (!state?.active) return;
    prevSwipeRef.current = { x: null, y: null, active: false };
    const t = (e.changedTouches && e.changedTouches[0]) || null;
    if (!t) return;
    const dx = t.clientX - state.x;
    const dy = t.clientY - state.y;
    // Ignore mostly-vertical swipes so the driver can still scroll the
    // sheet without triggering a photo swap.
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    // Swipe left → Photo 2, swipe right → Photo 1. No wraparound so a
    // deliberate flick always lands the driver on the intended photo.
    setPrevTab(dx < 0 ? 2 : 1);
  };
  const closePreviousImage = () => {
    setPrevOpen(false);
    // Revoke both blob URLs so we don't leak memory across opens.
    for (const url of Object.values(prevPhotos)) {
      if (url) { try { URL.revokeObjectURL(url); } catch { /* noop */ } }
    }
    setPrevPhotos({ 1: null, 2: null });
    setPrevErrors({ 1: null, 2: null });
    setPrevDeliveredAt(null);
  };

  const clearSig = () => {
    sigRef.current?.clear();
    setSignatureDataUrl(null);
  };

  const confirmSig = () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Please sign before confirming.");
      return;
    }
    setSignatureDataUrl(sigRef.current.getCanvas().toDataURL("image/png"));
    setSigOpen(false);
  };

  const cancelSig = () => {
    setSigOpen(false);
    // Restore previous sig (if any) on next open via useEffect below.
  };

  // When the overlay opens, lock the page scroll so swipes don't pull the page underneath.
  // Also dismiss any open Sonner toasts so the REFRIGERATED warning (or other stacked
  // alerts) don't cover the top bar of the signature pad.
  useEffect(() => {
    if (sigOpen) {
      toast.dismiss();
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [sigOpen]);

  // Size the underlying <canvas> element to its wrapper, and KEEP it sized
  // through orientation changes / mobile address-bar collapses.
  // We mutate the DOM element directly (instead of re-rendering the SignatureCanvas
  // component) because react-signature-canvas v1.x rebinds touch listeners on
  // every remount — which on iOS Safari sometimes prevents the first stroke
  // from registering. This avoids that whole class of bug.
  useLayoutEffect(() => {
    if (!sigOpen) return;
    const wrap = canvasWrapRef.current;
    if (!wrap) return;
    const apply = () => {
      const sig = sigRef.current;
      if (!sig) return;
      const canvas = sig.getCanvas();
      const rect = wrap.getBoundingClientRect();
      const w = Math.max(280, Math.floor(rect.width));
      const h = Math.max(280, Math.floor(rect.height));
      const ratio = Math.max(1, window.devicePixelRatio || 1);
      // Only resize when really different to preserve in-progress strokes.
      if (canvas.width !== Math.floor(w * ratio) || canvas.height !== Math.floor(h * ratio)) {
        canvas.width = Math.floor(w * ratio);
        canvas.height = Math.floor(h * ratio);
        const ctx = canvas.getContext("2d");
        ctx.setTransform(1, 0, 0, 1, 0, 0); // reset
        ctx.scale(ratio, ratio);
      }
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      // If user had a previous signature, repaint it so it's not lost.
      if (signatureDataUrl) {
        sig.fromDataURL(signatureDataUrl, { width: w, height: h });
      }
    };
    // Defer one frame so the portal+layout are committed.
    const raf = requestAnimationFrame(apply);
    const ro = new ResizeObserver(apply);
    ro.observe(wrap);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigOpen]);

  const submit = async () => {
    // Admin-on-behalf FAILED path — POST /fail with reason (+ optional photo).
    // Skips the whole POD checklist (photos, signature, copay) because the
    // driver didn't complete the drop — there's nothing to prove.
    if (failMode) {
      if (!failureReason.trim()) {
        toast.error("Please enter the reason the delivery failed.");
        return;
      }
      setSubmitting(true);
      try {
        const fd = new FormData();
        fd.append("reason", failureReason.trim());
        if (photo) fd.append("photo", photo);
        await api.post(`/orders/${order.id}/fail`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Marked as Failed");
        onCompleted?.();
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (!isCorrect) {
      // Driver-side delivery: ALL POD evidence is required. No photo, no
      // signature, no comment ⇒ no delivery. Super-admin / dispatcher can
      // still override missing fields through the Correct POD flow.
      const missing = [];
      if (!photo) missing.push("Photo 1");
      if (!photo2) missing.push("Photo 2");
      if (isControlled && !photo3) missing.push("Recipient ID photo");
      if (requireSignature && !signatureDataUrl) missing.push("Signature");
      if (!comment.trim()) missing.push("Comments");
      if (missing.length) {
        toast.error(`Cannot mark as delivered — missing: ${missing.join(", ")}`);
        return;
      }
      if (requiresCopay && copayCollected === null) {
        toast.error(`Please confirm whether the $${copayAmount.toFixed(2)} copay was collected.`);
        return;
      }
    } else {
      // Correction mode — require at least ONE field to be supplied OR a
      // status flip so we never submit an empty form.
      const statusChanged = newStatus && newStatus !== (order?.status || "delivered");
      if (!photo && !photo2 && !photo3 && !signatureDataUrl && !comment.trim() && !statusChanged) {
        toast.error("Provide at least one POD field to correct or switch the status.");
        return;
      }
      if (statusChanged && newStatus === "failed" && !failureReason.trim()) {
        toast.error("Please enter a reason when switching status to Failed.");
        return;
      }
    }
    setSubmitting(true);
    try {
      // Build a portable "parts" map. We keep raw Blob/File instances so the
      // optimistic queue can persist them to IndexedDB (FormData cannot be
      // serialized to disk — we re-build it just-in-time per upload attempt).
      const parts = { comment };
      if (photo) parts.photo = photo;
      if (photo2) parts.photo2 = photo2;
      if (photo3) parts.photo3 = photo3;
      if (requiresCopay) {
        parts.copay_collected = copayCollected ? "true" : "false";
        parts.copay_amount = String(copayAmount);
        parts.copay_method = "cash";
      }
      if (signatureDataUrl) {
        const blob = await (await fetch(signatureDataUrl)).blob();
        // Wrap in a File so the original .png filename survives the round-trip.
        parts.signature = new File([blob], "signature.png", { type: blob.type || "image/png" });
      }
      // Status override (correct mode). Only send when the operator picked
      // a value different from the order's current status.
      if (isCorrect && newStatus && newStatus !== (order?.status || "delivered")) {
        parts.new_status = newStatus;
        if (newStatus === "failed" && failureReason.trim()) {
          parts.failure_reason = failureReason.trim();
        }
      }
      // OPTIMISTIC PATH (driver) — hand the parts to the parent and bail.
      // The parent enqueues to IndexedDB, closes the sheet immediately, and
      // a background sweeper retries until the upload lands.
      if (!isCorrect && typeof onSubmitInBackground === "function") {
        onSubmitInBackground(order.id, parts);
        return;
      }
      // Synchronous path (POD correction / fallback) — still uses FormData.
      const fd = new FormData();
      for (const [k, v] of Object.entries(parts)) fd.append(k, v);
      const endpoint = isCorrect ? `/orders/${order.id}/pod` : `/orders/${order.id}/deliver`;
      await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(isCorrect ? "POD updated" : "Delivery completed");
      onCompleted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && (
    failMode
      ? !!failureReason.trim()
      : isCorrect
      ? (
          !!photo || !!photo2 || !!photo3 || !!signatureDataUrl || !!comment.trim() ||
          (newStatus && newStatus !== (order?.status || "delivered") &&
            (newStatus !== "failed" || !!failureReason.trim()))
        )
      : (
          photo && photo2 &&
          (!isControlled || !!photo3) &&
          (!requireSignature || !!signatureDataUrl) &&
          !!comment.trim() &&
          (!requiresCopay || copayCollected !== null)
        )
  );

  return (
    <div className="space-y-5" data-testid="pod-capture">
      {allowStatusPicker && !isCorrect && (
        <div className="rounded-xl border border-border bg-secondary/40 p-3" data-testid="pod-deliver-action">
          <div className="text-xs font-medium mb-2 uppercase tracking-wider text-muted-foreground">Outcome</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setDeliverAction("delivered")}
              className={`px-3 py-2 rounded-full text-sm font-medium border transition-all ${
                deliverAction === "delivered"
                  ? "border-[#16A34A] bg-[#dcfce7] text-[#166534]"
                  : "border-border bg-card text-muted-foreground hover:border-[#16A34A]/40"
              }`}
              data-testid="pod-deliver-action-delivered"
            >
              ✓ Delivered
            </button>
            <button
              type="button"
              onClick={() => setDeliverAction("failed")}
              className={`px-3 py-2 rounded-full text-sm font-medium border transition-all ${
                deliverAction === "failed"
                  ? "border-[#DC2626] bg-[#fee2e2] text-[#991B1B]"
                  : "border-border bg-card text-muted-foreground hover:border-[#DC2626]/40"
              }`}
              data-testid="pod-deliver-action-failed"
            >
              ✕ Failed
            </button>
          </div>
          {failMode && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Failed stops need a reason (and an optional photo). Photos, signature and copay are not required.
            </p>
          )}
        </div>
      )}
      {failMode && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3" data-testid="pod-fail-form">
          <Label htmlFor="pod-fail-reason" className="text-xs">Failure reason <span className="text-destructive">*</span></Label>
          <input
            id="pod-fail-reason"
            type="text"
            value={failureReason}
            onChange={(e) => setFailureReason(e.target.value)}
            placeholder="e.g. Customer not home, wrong address, refused delivery…"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            data-testid="pod-fail-reason"
          />
          <p className="mt-3 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Optional photo</p>
          <p className="text-[11px] text-muted-foreground mb-2">Snapshot of an empty doorstep, note left, damaged package, etc. — helps the pharmacy triage the retry.</p>
          <PhotoSlot label="Fail photo" preview={photoPreview} onChange={onPhoto} onClear={() => clearPhoto(1)} testId="pod-fail-photo-input" />
        </div>
      )}
      {!failMode && isCorrect && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs flex items-start gap-2" data-testid="pod-correct-notice">
          <span className="text-base leading-none">⚠️</span>
          <span>
            <span className="font-semibold">Correcting POD</span> — only the fields you change will be overwritten. Flip <span className="font-semibold">Delivered ↔ Failed</span> below if a driver closed this stop with the wrong outcome. Every correction is logged in the order history.
          </span>
        </div>
      )}
      {isCorrect && (
        <div className="rounded-xl border border-border bg-secondary/40 p-3" data-testid="pod-correct-status">
          <div className="text-xs font-medium mb-2 uppercase tracking-wider text-muted-foreground">Final delivery status</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setNewStatus("delivered")}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                newStatus === "delivered"
                  ? "border-[#16A34A] bg-[#dcfce7] text-[#166534]"
                  : "border-border bg-card text-muted-foreground hover:border-[#16A34A]/40"
              }`}
              data-testid="pod-correct-status-delivered"
            >
              ✓ Delivered
            </button>
            <button
              type="button"
              onClick={() => setNewStatus("failed")}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                newStatus === "failed"
                  ? "border-[#DC2626] bg-[#fee2e2] text-[#991B1B]"
                  : "border-border bg-card text-muted-foreground hover:border-[#DC2626]/40"
              }`}
              data-testid="pod-correct-status-failed"
            >
              ✕ Failed
            </button>
          </div>
          {newStatus === "failed" && (
            <div className="mt-3">
              <Label htmlFor="pod-correct-failure-reason" className="text-xs">Failure reason {newStatus !== (order?.status || "delivered") && <span className="text-destructive">*</span>}</Label>
              <input
                id="pod-correct-failure-reason"
                type="text"
                value={failureReason}
                onChange={(e) => setFailureReason(e.target.value)}
                placeholder="e.g. Customer not home, wrong address, refused delivery…"
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm"
                data-testid="pod-correct-failure-reason"
              />
            </div>
          )}
          {newStatus !== (order?.status || "delivered") && (
            <div className="mt-2 text-[11px] text-amber-800">
              Status will change from <span className="font-semibold">{(order?.status || "delivered").toUpperCase()}</span> to <span className="font-semibold">{newStatus.toUpperCase()}</span> on save.
            </div>
          )}
        </div>
      )}
      {requiresCopay && (
        <div
          className={`rounded-xl border-2 p-4 ${copayCollected === true ? "border-[#7A9B7A] bg-[#e3f0e1]" : copayCollected === false ? "border-[#D95D39] bg-[#fde3dc]" : "border-[#FF6D00] bg-[#fff4d6]"}`}
          data-testid="pod-copay-prompt"
        >
          <div className="flex items-start gap-2.5">
            <DollarSign className={`h-5 w-5 mt-0.5 shrink-0 ${copayCollected === true ? "text-[#234123]" : copayCollected === false ? "text-[#a73a1c]" : "text-[#9c4500]"}`} />
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold ${copayCollected === true ? "text-[#234123]" : copayCollected === false ? "text-[#a73a1c]" : "text-[#9c4500]"}`}>
                Did you collect <span className="font-mono-num">${copayAmount.toFixed(2)}</span> copay?
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Cash collection only.</div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={copayCollected === true ? "default" : "outline"}
                  className={copayCollected === true ? "bg-[#234123] hover:bg-[#1a3119] text-white" : ""}
                  onClick={() => setCopayCollected(true)}
                  data-testid="pod-copay-collected"
                >
                  ✓ Collected (cash)
                </Button>
                <Button
                  type="button"
                  variant={copayCollected === false ? "default" : "outline"}
                  className={copayCollected === false ? "bg-[#a73a1c] hover:bg-[#8a2f17] text-white" : ""}
                  onClick={() => setCopayCollected(false)}
                  data-testid="pod-copay-declined"
                >
                  ✕ Not collected
                </Button>
              </div>
              {copayCollected === false && (
                <div className="mt-2 text-[11px] text-[#a73a1c] bg-white/60 rounded-md px-2 py-1.5" data-testid="pod-copay-leave-meds-hint">
                  You can still leave the medication and complete the delivery. The pharmacy will follow up.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!failMode && (<>
      {requireSignature && (
      <div data-testid="pod-signature-block">
        <Label>Recipient signature {!isCorrect && <span className="text-destructive">*</span>}</Label>
        <div className="mt-1.5">
          {signatureDataUrl ? (
            <div className="relative rounded-xl border border-border bg-secondary/30 p-2 flex items-center justify-center">
              <img src={signatureDataUrl} alt="Signature" className="h-24 max-w-full object-contain" data-testid="pod-signature-preview" />
              <button
                type="button"
                onClick={clearSig}
                className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80"
                data-testid="pod-sig-remove"
                aria-label="Remove signature"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSigOpen(true)}
                className="absolute bottom-1.5 right-1.5 rounded-full h-7 px-2.5 text-[11px]"
                data-testid="pod-sig-edit"
              >
                <PenLine className="h-3 w-3 mr-1" /> Edit
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              onClick={() => setSigOpen(true)}
              className="w-full h-14 rounded-xl border-2 border-dashed border-border bg-secondary/40 hover:bg-secondary text-foreground"
              variant="ghost"
              data-testid="pod-sig-open"
            >
              <PenLine className="h-5 w-5 mr-2" />
              Tap to sign — fullscreen
            </Button>
          )}
        </div>
      </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <PhotoSlot label={isCorrect ? "Photo 1" : "Photo 1 *"} preview={photoPreview} onChange={onPhoto} onClear={() => clearPhoto(1)} testId="pod-photo-input" />
        <PhotoSlot label={isCorrect ? "Photo 2" : "Photo 2 *"} preview={photoPreview2} onChange={onPhoto2} onClear={() => clearPhoto(2)} testId="pod-photo-input-2" />
      </div>

      {/* Compare previous image — driver visual sanity check on doorstep */}
      <Button
        type="button"
        variant="outline"
        onClick={openPreviousImage}
        className="w-full h-11 rounded-full border-2 border-dashed border-amber-400 bg-amber-100 text-amber-900 hover:bg-amber-200 hover:text-amber-900 dark:bg-amber-500/20 dark:text-amber-100 dark:border-amber-400/60 dark:hover:bg-amber-500/30"
        data-testid="pod-compare-previous-btn"
      >
        <ImageIcon className="h-4 w-4 mr-2" /> Compare previous image
      </Button>

      {isControlled && (
        <div className="rounded-xl border-2 border-destructive/40 bg-destructive/5 p-3" data-testid="pod-id-photo-section">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold uppercase tracking-wider">CII</span>
            <span className="text-sm font-medium">Recipient ID photo {isCorrect ? <span className="text-muted-foreground font-normal text-xs">(optional)</span> : <span className="text-destructive">*required</span>}</span>
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">CONTROLLED medication — photograph the recipient's government-issued ID for compliance.</p>
          <PhotoSlot label="ID photo" preview={photoPreview3} onChange={onPhoto3} onClear={() => clearPhoto(3)} testId="pod-photo-input-3" />
        </div>
      )}

      <div>
        <Label htmlFor="pod-cmt">Comments <span className="text-destructive">*</span></Label>
        <Textarea id="pod-cmt" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Left with neighbor / signed by Mr. Smith…" data-testid="pod-comment" />
      </div>

      {!isCorrect && !canSubmit && !submitting && (() => {
        const missing = [];
        if (!photo) missing.push("Photo 1");
        if (!photo2) missing.push("Photo 2");
        if (isControlled && !photo3) missing.push("ID photo");
        if (requireSignature && !signatureDataUrl) missing.push("Signature");
        if (!comment.trim()) missing.push("Comments");
        if (requiresCopay && copayCollected === null) missing.push("Copay confirmation");
        if (missing.length === 0) return null;
        return (
          <div
            className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs"
            data-testid="pod-missing-checklist"
          >
            <div className="font-semibold mb-0.5">Still missing to complete this delivery:</div>
            <ul className="list-disc list-inside space-y-0.5">
              {missing.map((m) => <li key={m}>{m}</li>)}
            </ul>
          </div>
        );
      })()}
      </>)}

      <Button onClick={submit} disabled={!canSubmit} className="w-full h-14 rounded-full text-base" data-testid="pod-submit">
        <CheckCircle2 className="h-5 w-5 mr-2" /> {submitting ? (failMode ? "Marking failed…" : isCorrect ? "Saving…" : "Submitting…") : (failMode ? "Mark as Failed" : isCorrect ? "Save POD correction" : "Complete delivery")}
      </Button>

      {sigOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-white flex flex-col pointer-events-auto"
          style={{ background: "#fff" }}
          data-testid="pod-sig-overlay"
          role="dialog"
          aria-label="Signature pad"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-3 text-foreground border-b border-border bg-white">
            <button
              type="button"
              onClick={cancelSig}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full hover:bg-secondary"
              data-testid="pod-sig-cancel"
            >
              <X className="h-4 w-4" /> Cancel
            </button>
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Recipient signature</div>
            <button
              type="button"
              onClick={() => sigRef.current?.clear()}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full hover:bg-secondary"
              data-testid="pod-sig-overlay-clear"
            >
              <RotateCcw className="h-4 w-4" /> Clear
            </button>
          </div>

          {/* Canvas — fills the rest of the screen.
              The SignatureCanvas renders unconditionally; the useLayoutEffect above
              sizes its underlying <canvas> element to the wrapper. */}
          <div ref={canvasWrapRef} className="flex-1 relative bg-white overflow-hidden">
            <SignatureCanvas
              ref={sigRef}
              penColor="#000000"
              minWidth={1.8}
              maxWidth={3.2}
              backgroundColor="rgba(255,255,255,0)"
              canvasProps={{
                className: "absolute inset-0",
                style: { width: "100%", height: "100%", touchAction: "none", display: "block" },
                "data-testid": "pod-signature-canvas",
              }}
            />
            {/* Visual guide line for where to sign */}
            <div className="pointer-events-none absolute left-6 right-6 bottom-24 border-b border-dashed border-black/30" />
            <div className="pointer-events-none absolute left-6 right-6 bottom-20 text-[11px] uppercase tracking-[0.2em] text-black/45 text-center">
              X · sign above the line
            </div>
          </div>

          {/* Bottom action */}
          <div className="px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-white border-t border-border">
            <button
              type="button"
              onClick={confirmSig}
              className="w-full h-14 rounded-full bg-primary text-primary-foreground font-semibold text-base flex items-center justify-center gap-2 active:scale-[.98] transition-transform"
              data-testid="pod-sig-confirm"
            >
              <CheckCircle2 className="h-5 w-5" /> Done — use this signature
            </button>
          </div>
        </div>,
        document.body
      )}

      {prevOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4 pointer-events-auto"
          onClick={closePreviousImage}
          data-testid="pod-previous-image-overlay"
          role="dialog"
          aria-label="Previous delivery photo"
        >
          <div
            className="relative w-full max-w-lg bg-card text-foreground rounded-2xl shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div>
                <div className="text-sm font-semibold">Previous delivery photo</div>
                <div className="text-[11px] text-muted-foreground">
                  {order?.customer_name}
                  {prevDeliveredAt && ` · Delivered ${fmtDateTime(prevDeliveredAt)}`}
                </div>
              </div>
              <button
                type="button"
                onClick={closePreviousImage}
                className="h-8 w-8 grid place-items-center rounded-full hover:bg-secondary"
                data-testid="pod-previous-image-close"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Tab bar — Photo 1 / Photo 2. Photo 1 must be present (otherwise the whole
                modal shows the NO PREVIOUS DELIVERY panel below). Photo 2 tab is disabled-styled
                but still tappable so drivers see WHY the switch is a no-op ("not captured on
                previous delivery"). Drivers can ALSO swipe the image area left/right to flip
                between tabs one-handed — see onTouch handlers on the image container below. */}
            {prevErrors[1] !== "NO_PREVIOUS_DELIVERY" && (
              <>
                <div className="px-4 pt-3 flex items-center gap-2" role="tablist" aria-label="Previous delivery photos">
                  {[1, 2].map((slot) => {
                    const isActive = prevTab === slot;
                    const missing = prevErrors[slot] === "NO_PREVIOUS_PHOTO_2" || prevErrors[slot] === "NO_PREVIOUS_DELIVERY";
                    return (
                      <button
                        key={slot}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => setPrevTab(slot)}
                        data-testid={`pod-previous-image-tab-${slot}`}
                        className={
                          `flex-1 h-9 rounded-full text-xs font-semibold uppercase tracking-wide transition-colors border ${
                            isActive
                              ? "bg-amber-200 text-amber-900 border-amber-400 dark:bg-amber-500/30 dark:text-amber-100 dark:border-amber-400/60"
                              : "bg-transparent text-muted-foreground border-border hover:bg-secondary"
                          } ${missing && !isActive ? "opacity-70" : ""}`
                        }
                      >
                        Photo {slot}
                      </button>
                    );
                  })}
                </div>
                {/* Swipe hint — small, low-key. Only shown once the tabs are visible. */}
                <div className="text-center text-[10px] text-muted-foreground pt-1.5 select-none pointer-events-none" data-testid="pod-previous-image-swipe-hint">
                  ← swipe to switch photos →
                </div>
              </>
            )}

            <div
              className="p-4 min-h-[220px] grid place-items-center touch-pan-y"
              onTouchStart={onPrevTouchStart}
              onTouchEnd={onPrevTouchEnd}
              data-testid="pod-previous-image-swipe-area"
            >
              {prevBusy && (
                <div className="flex flex-col items-center gap-2 text-muted-foreground text-sm" data-testid="pod-previous-image-busy">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  Loading previous image…
                </div>
              )}
              {!prevBusy && prevErrors[1] === "NO_PREVIOUS_DELIVERY" && (
                <div
                  className="w-full text-center py-6 px-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 text-amber-900"
                  data-testid="pod-previous-image-none"
                >
                  <div className="text-base font-semibold uppercase tracking-wide">NO PREVIOUS DELIVERY</div>
                  <div className="text-xs mt-1 opacity-80">This patient has no earlier delivered order on file to compare against.</div>
                </div>
              )}
              {!prevBusy && prevErrors[1] !== "NO_PREVIOUS_DELIVERY" && prevErrors[prevTab] === "NO_PREVIOUS_PHOTO_2" && (
                <div
                  className="w-full text-center py-6 px-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 text-amber-900"
                  data-testid="pod-previous-image-none-photo2"
                >
                  <div className="text-base font-semibold uppercase tracking-wide">Photo 2 not captured</div>
                  <div className="text-xs mt-1 opacity-80">The driver on the previous delivery only captured Photo 1.</div>
                </div>
              )}
              {!prevBusy && prevErrors[1] !== "NO_PREVIOUS_DELIVERY" && prevErrors[prevTab] === "GENERIC" && (
                <div
                  className="w-full text-center py-6 px-4 rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm"
                  data-testid="pod-previous-image-error"
                >
                  Could not load Photo {prevTab}. Please try again.
                </div>
              )}
              {!prevBusy && !prevErrors[prevTab] && prevPhotos[prevTab] && (
                <img
                  src={prevPhotos[prevTab]}
                  alt={`Previous delivery Photo ${prevTab}`}
                  className="w-full max-h-[65vh] object-contain rounded-lg border border-border bg-black/5"
                  data-testid={`pod-previous-image-photo-${prevTab}`}
                />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
